/* ============================================================
   cartPickupView — 3830fe0d: pure view model for the pickup-point
   pick at cart checkout.

   Quote-print lines ride their own per-line delivery choice made on
   the quote page (AddQuotePrintDto); the pickup POINT itself is
   chosen here at checkout — G8-D2 server-side applies
   CheckoutDto.PickupPointId to its performer's group (point belongs
   to exactly one provider). Parity with the pre-cart quote-page flow:
   a Pickup line without a chosen point blocks checkout.

   Pure functions; CartPage owns the state. Node-tested.
   ============================================================ */

import type { CartItemView } from '../types/product.ts';
import { normalizeDeliveryMethod } from '../types/product.ts';
import type { WebDeliveryMethod } from '../types/product.ts';

/** Numeric CartItemKind.QuotePrint (defensive: number or numeric string). */
const QUOTE_PRINT_KIND = 1;

export interface PickupNeed {
  /** Performer whose Pickup print line(s) await a point; null = none. */
  performerId: string | null;
  performerName: string | null;
}

/** The performer needing a pickup point: the first Pickup quote-print line. */
export function pickupPerformer(
  items: readonly CartItemView[] | null | undefined,
): PickupNeed | null {
  const line = (items ?? []).find(
    (i) =>
      Number(i.kind ?? 0) === QUOTE_PRINT_KIND
      && normalizeDeliveryMethod(i.deliveryMethod ?? null) === 'Pickup'
      && typeof i.performerId === 'string'
      && i.performerId.length > 0,
  );
  return line
    ? { performerId: line.performerId ?? null, performerName: line.providerName ?? null }
    : null;
}

/**
 * Blocking reason for checkout while a Pickup line has no usable point:
 * none chosen (or still loading — the first point auto-selects, so the
 * block is transient), or the performer has no active points at all.
 * Null = clear.
 */
export function pickupBlock(
  need: PickupNeed | null,
  chosenPointId: string | null,
  points: readonly unknown[] | null,
): string | null {
  if (need === null) return null;
  if (points !== null && points.length === 0)
    return 'У исполнителя нет активных пунктов самовывоза — удалите строку и добавьте её снова с доставкой курьером';
  // Loading or loaded-but-unchosen: the first point auto-selects on load,
  // so this block is transient and keeps checkout from racing the fetch.
  if (!chosenPointId)
    return 'Выберите пункт самовывоза';
  return null;
}

/* ── b0bef46e: unified pickup-point contract ───────────────────

   One ПВЗ map for every non-courier checkout method: the carrier
   point picked on the map is the single receiving point. Pure
   helpers; CartPage owns the state. Node-tested. */

/** The checkout map is the selection surface for every self-pickup method; courier-by-address keeps the plain address form. */
export function showPickupMap(method: WebDeliveryMethod): boolean {
  return method !== 'Courier';
}

/**
 * Blocking reason while the ПВЗ chip is active without a chosen point:
 * a DropoffPoint order without a receiving ПВЗ is undeliverable (the
 * server rejects it too — mirrors the CheckoutAsync guard).
 */
export function carrierPointBlock(
  method: WebDeliveryMethod,
  carrierPointChosen: boolean,
): string | null {
  if (method === 'DropoffPoint' && !carrierPointChosen)
    return 'Выберите пункт выдачи на карте — или переключитесь на курьера';
  return null;
}
